import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addComment,
  createRequest,
  getRequest,
  listRequests,
  patchRequest,
  STATUS_LABELS,
  type FeatureRequest,
} from "../api/requests";
import { getMyPermissions } from "../api/users";

const STATUS_STYLES: Record<FeatureRequest["status"], string> = {
  new: "bg-blue-100 text-blue-700",
  accepted: "bg-indigo-100 text-indigo-700",
  in_progress: "bg-amber-100 text-amber-700",
  testing: "bg-purple-100 text-purple-700",
  done: "bg-green-100 text-green-700",
  declined: "bg-gray-200 text-gray-600",
};

function EtaBadge({ req }: { req: FeatureRequest }) {
  if (req.status === "done" || req.status === "declined" || !req.eta_at) return null;
  const ms = new Date(req.eta_at).getTime() - Date.now();
  const hours = Math.floor(ms / 3_600_000);
  const overdue = ms < 0;
  const cls = overdue
    ? "bg-red-100 text-red-700"
    : hours < 24
      ? "bg-amber-100 text-amber-700"
      : "bg-green-100 text-green-700";
  const label = overdue
    ? "ETA passed — being addressed"
    : hours >= 48
      ? `~${Math.round(hours / 24)} days left`
      : `~${Math.max(1, hours)}h left`;
  return (
    <span className={`text-xs rounded px-2 py-0.5 whitespace-nowrap ${cls}`} title={`Estimated completion: ${new Date(req.eta_at).toLocaleString()}${req.eta_note ? ` — ${req.eta_note}` : ""}`}>
      {label}
    </span>
  );
}

function NewRequestForm({ onDone }: { onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("feature");
  const [urgent, setUrgent] = useState(false);
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => createRequest({ title, description: description || undefined, category, urgent }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
      setTitle("");
      setDescription("");
      setUrgent(false);
      onDone();
    },
  });

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <h2 className="font-semibold mb-2">New request</h2>
      <div className="grid gap-2">
        <input
          className="border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="What do you need? (short title)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
        />
        <textarea
          className="border border-gray-300 rounded px-3 py-2 text-sm min-h-[90px]"
          placeholder="Details — what should it do, and why it matters to you"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex items-center gap-4 text-sm">
          <select
            className="border border-gray-300 rounded px-2 py-1.5"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="feature">New feature</option>
            <option value="change">Change to existing</option>
            <option value="problem">Problem / not working</option>
          </select>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
            Urgent for my business
          </label>
        </div>
        <p className="text-xs text-gray-500">
          Every request gets an estimated completion window of 72 hours. Complex requests may
          receive a revised estimate (with the reason) within 24 hours.
        </p>
        {submit.isError && <p className="text-sm text-red-600">{(submit.error as Error).message}</p>}
        <div>
          <button
            className="bg-brand text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            disabled={title.trim().length < 3 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetail({ id, isAdmin }: { id: number; isAdmin: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["feature-request", id],
    queryFn: () => getRequest(id),
  });
  const [comment, setComment] = useState("");
  const [etaNote, setEtaNote] = useState("");
  const [etaAt, setEtaAt] = useState("");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["feature-request", id] });
    qc.invalidateQueries({ queryKey: ["feature-requests"] });
  };
  const commentMut = useMutation({
    mutationFn: () => addComment(id, comment),
    onSuccess: () => {
      setComment("");
      refresh();
    },
  });
  const statusMut = useMutation({
    mutationFn: (status: string) => patchRequest(id, { status }),
    onSuccess: refresh,
  });
  const etaMut = useMutation({
    mutationFn: () => patchRequest(id, { eta_at: etaAt, eta_note: etaNote }),
    onSuccess: () => {
      setEtaAt("");
      setEtaNote("");
      refresh();
    },
  });

  if (isLoading || !data) return <p className="text-gray-500 text-sm p-3">Loading…</p>;

  return (
    <div className="border-t border-gray-100 bg-gray-50 p-4 text-sm">
      {data.description && <p className="mb-3 whitespace-pre-wrap text-gray-700">{data.description}</p>}
      {data.declined_reason && data.status === "declined" && (
        <p className="mb-3 text-gray-600">
          <span className="font-medium">Reason:</span> {data.declined_reason}
        </p>
      )}
      <h3 className="font-semibold mb-2 text-gray-700">Progress</h3>
      <ol className="space-y-2 mb-4">
        {data.timeline.map((u) => (
          <li key={u.update_id} className="flex gap-2">
            <span className="text-gray-400 whitespace-nowrap">
              {new Date(u.created_at).toLocaleString()}
            </span>
            <span className={u.author_role === "team" ? "text-gray-800" : "text-blue-800"}>
              {u.author_role === "team" ? "Team: " : "You: "}
              {u.note}
            </span>
          </li>
        ))}
      </ol>
      <div className="flex gap-2 mb-2">
        <input
          className="border border-gray-300 rounded px-3 py-1.5 flex-1"
          placeholder="Add a comment or clarification…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <button
          className="bg-gray-800 text-white rounded px-3 py-1.5 disabled:opacity-50"
          disabled={!comment.trim() || commentMut.isPending}
          onClick={() => commentMut.mutate()}
        >
          Send
        </button>
      </div>
      {isAdmin && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <p className="text-xs uppercase text-gray-400 mb-1">Team controls</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {(["accepted", "in_progress", "testing", "done", "declined"] as const).map((s) => (
              <button
                key={s}
                className={`rounded px-2.5 py-1 text-xs border ${data.status === s ? "bg-gray-800 text-white" : "bg-white hover:bg-gray-100"}`}
                disabled={statusMut.isPending}
                onClick={() => statusMut.mutate(s)}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="datetime-local"
              className="border border-gray-300 rounded px-2 py-1 text-xs"
              value={etaAt}
              onChange={(e) => setEtaAt(e.target.value)}
            />
            <input
              className="border border-gray-300 rounded px-2 py-1 text-xs w-64"
              placeholder="Reason for new ETA (required, shown to client)"
              value={etaNote}
              onChange={(e) => setEtaNote(e.target.value)}
            />
            <button
              className="bg-white border rounded px-2.5 py-1 text-xs hover:bg-gray-100 disabled:opacity-50"
              disabled={!etaAt || !etaNote.trim() || etaMut.isPending}
              onClick={() => etaMut.mutate()}
            >
              Revise ETA
            </button>
          </div>
          {(statusMut.isError || etaMut.isError) && (
            <p className="text-xs text-red-600 mt-1">
              {((statusMut.error || etaMut.error) as Error).message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function RequestsPage() {
  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const { data: me } = useQuery({ queryKey: ["me-permissions"], queryFn: getMyPermissions });
  const isAdmin = me?.is_admin ?? false;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["feature-requests"],
    queryFn: listRequests,
    refetchInterval: 30_000,
  });

  if (isLoading) return <p className="text-gray-500">Loading requests…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;

  const rows = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold">Feature Requests</h1>
        <button
          className="bg-brand text-white rounded px-4 py-2 text-sm"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? "Close" : "+ New Request"}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Ask for new features, changes, or report problems — and track progress live. Standard
        turnaround: 72 hours.
      </p>

      {showForm && <NewRequestForm onDone={() => setShowForm(false)} />}

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No requests yet. Need something changed or added? Hit “New Request”.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {rows.map((r) => (
            <div key={r.request_id}>
              <button
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50"
                onClick={() => setOpenId(openId === r.request_id ? null : r.request_id)}
              >
                <span className={`text-xs rounded px-2 py-0.5 whitespace-nowrap ${STATUS_STYLES[r.status]}`}>
                  {STATUS_LABELS[r.status]}
                </span>
                <span className="flex-1 font-medium text-sm">
                  {r.urgent && <span className="text-red-600 mr-1">●</span>}
                  {r.title}
                  {isAdmin && <span className="ml-2 text-xs text-gray-400">#{r.request_id} · usr {r.created_by}</span>}
                </span>
                <EtaBadge req={r} />
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </button>
              {openId === r.request_id && <RequestDetail id={r.request_id} isAdmin={isAdmin} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
